import { useEffect, useRef, useState } from 'react';

import { Popover } from 'react-tiny-popover';

import ListingSearchSelect from '@components/Select/Select';
import { SEARCH_LENGTH } from '@utils/utils';
import useIsMounted from '@hooks/useIsMounted';
import { SearchResultError } from '@components/ui/search/SearchBarInput/SearchBarSearchInput/SearchBarSearchInputStyle';
import CategoryBaseHotelsList from '@components/views/landing/components/booker/HotelTabContent/HotelSearchDrawer/components/CategoryBaseHotelsList';

import { ListingSearchLocationProps } from './types';
import {
  LocationAnimationErrorBox,
  LocationItemsList,
} from './SearchLocationStyle';

const DesktopSearchLocation = ({
  label,
  text,
  data = [],
  handleLocationClicked,
  handleChange,
  onInputBlur,
  isLocationEntered,
  searchTextError,
  searchTextLoading,
}: React.PropsWithChildren<ListingSearchLocationProps>) => {
  const [selected, setSelectedLocation] = useState(false);

  // ====================================
  //  using hooks to store data and adding ref
  // ====================================

  const inputRef = useRef<HTMLInputElement>(null);
  const searchResultRef = useRef<any>(null);
  const [cursor, setCursor] = useState(-1);

  const toggleSelect = () => setSelectedLocation((val) => !val);

  const keyboardNavigation = (e: any) => {
    if (e.key === 'ArrowDown') {
      selected
        ? setCursor((c) => (c < data.length - 1 ? c + 1 : c))
        : toggleSelect();
    }

    if (e.key === 'ArrowUp') {
      setCursor((c) => (c > 0 ? c - 1 : 0));
    }

    if (e.key === 'Escape') {
      toggleSelect();
    }

    if (e.key === 'Enter' && cursor > 0) {
      handleLocationClicked(data[cursor]);
      toggleSelect();
    }
  };
  const scrollIntoView = (position: any) => {
    searchResultRef.current?.scrollTo({
      top: position,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (
      cursor < 0 ||
      cursor > data.length ||
      !searchResultRef ||
      !searchResultRef.current?.children
    )
      return;

    const listItems: any[] = Array?.from(searchResultRef.current?.children);

    listItems[cursor] && scrollIntoView(listItems[cursor].offsetTop);
  }, [cursor]);

  const shouldOpenResultModal =
    selected && text?.length >= SEARCH_LENGTH && !searchTextLoading
      ? true
      : false;

  const [parentRef, parentElement] = useIsMounted<HTMLDivElement>();

  return (
    <div ref={parentRef}>
      {parentElement ? (
        <Popover
          positions={['bottom', 'left']}
          align='start'
          padding={10}
          containerStyle={{
            zIndex: '101',
            backgroundColor: 'white',
            width: '540px',
            display: 'flex',
            justifyContent: 'center',
            margin: '0px',
            boxShadow: '0 20px 40px 0 rgba(0, 0, 0, 0.2)',
            borderRadius: '8px',
          }}
          isOpen={shouldOpenResultModal}
          parentElement={parentElement}
          onClickOutside={() => {
            setSelectedLocation(false);
          }}
          content={
            searchTextError ? (
              <SearchResultError>
                Aradığınız kriterlere uygun sonuç bulunamadı.
              </SearchResultError>
            ) : (
              <LocationItemsList ref={searchResultRef}>
                <CategoryBaseHotelsList
                  locations={data}
                  onChange={(item) => {
                    handleLocationClicked(item);
                    setSelectedLocation(false);
                  }}
                />
              </LocationItemsList>
            )
          }
        >
          <LocationAnimationErrorBox
            data-testid='location-box-error'
            className={!isLocationEntered ? 'location-animation-error' : ''}
          >
            <ListingSearchSelect
              label={label}
              text={text}
              icon='location-pin'
              selected={selected}
              onSelect={toggleSelect}
              isInput={true}
              onInputChange={handleChange}
              inputRef={inputRef}
              onInputBlur={onInputBlur}
              onFocus={() => setSelectedLocation(true)}
              onInputKeyDown={(e: any) => keyboardNavigation(e)}
              type={text ? undefined : 'hotel'}
            />
          </LocationAnimationErrorBox>
        </Popover>
      ) : null}
    </div>
  );
};

export default DesktopSearchLocation;
